import { useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import type { CampaignTimelineRow } from '@/models';
import { buildChannelScale, OTHER_CHANNEL } from './channelColour';

export type Zoom = 'quarter' | 'month' | 'week';
export type SortKey = 'start' | 'net' | 'duration' | 'campaign';
export type StatusKey = 'all' | 'live' | 'upcoming' | 'ended';

export interface TimelineFilters {
  zoom: Zoom;
  sort: SortKey;
  status: StatusKey;
  channel: string | null;
  search: string;
  /** `YYYY-Qn`, or null for the whole booked range. */
  quarter: string | null;
}

/** Quarter key for a date, in the same `YYYY-Qn` form the filter uses. */
export const quarterKey = (date: Dayjs): string =>
  `${date.format('YYYY')}-Q${Math.floor(date.month() / 3) + 1}`;

export const currentQuarterKey = (): string => quarterKey(dayjs());

/** Start/end of a `YYYY-Qn` key, or null if it does not parse. */
export const quarterRange = (key: string): { start: Dayjs; end: Dayjs } | null => {
  const match = /^(\d{4})-Q([1-4])$/.exec(key);
  if (!match) return null;
  const start = dayjs(`${match[1]}-01-01`).add((Number(match[2]) - 1) * 3, 'month');
  return { start: start.startOf('month'), end: start.add(2, 'month').endOf('month') };
};

/** Every quarter the data actually spans, so the picker never offers an empty one. */
export const quartersIn = (rows: CampaignTimelineRow[]): string[] => {
  const keys = new Set<string>();
  rows.forEach((row) => {
    if (!row.start_date || !row.end_date) return;
    let cursor = dayjs(row.start_date).startOf('month');
    const end = dayjs(row.end_date);
    if (!cursor.isValid() || !end.isValid()) return;
    // A flight spanning several quarters belongs to each of them.
    while (!cursor.isAfter(end, 'month')) {
      keys.add(quarterKey(cursor));
      cursor = cursor.add(1, 'month');
    }
  });
  return Array.from(keys).sort();
};

/** One flight, resolved to pixel-independent percentages of the axis. */
export interface TimelineBar {
  key: string;
  row: CampaignTimelineRow;
  left: number;
  width: number;
  colour: string;
  status: Exclude<StatusKey, 'all'>;
  start: Dayjs;
  end: Dayjs;
  days: number;
  /** The flight runs on past an edge of the visible axis -- drawn as a flat,
   *  open end so a clipped bar is not misread as the campaign stopping there. */
  clippedStart: boolean;
  clippedEnd: boolean;
}

export interface AxisTick {
  key: string;
  label: string;
  /** Share of the whole axis, 0–1. Ticks are weighted by real elapsed days so a
   *  28-day February never renders as wide as a 31-day March. */
  weight: number;
  major: boolean;
}

/** A flight counts as live on its start and end dates, not just between them. */
const statusOf = (start: Dayjs, end: Dayjs, now: Dayjs): Exclude<StatusKey, 'all'> => {
  if (now.isBefore(start, 'day')) return 'upcoming';
  if (now.isAfter(end, 'day')) return 'ended';
  return 'live';
};

const DAY_MS = 86_400_000;

/**
 * Builds every derived view of the timeline in one pass.
 *
 * Filtering happens before the axis is measured, so zooming into "live only"
 * rescales the chart to the flights on screen rather than leaving them crushed
 * into a corner of the full three-year range.
 */
export const useTimelineModel = (rows: CampaignTimelineRow[], filters: TimelineFilters) => {
  return useMemo(() => {
    const now = dayjs();

    const dated = rows
      .filter((row) => row.start_date && row.end_date)
      .map((row) => {
        const start = dayjs(row.start_date as string).startOf('day');
        const end = dayjs(row.end_date as string).startOf('day');
        return { row, start, end, valid: start.isValid() && end.isValid() && !end.isBefore(start) };
      })
      .filter((entry) => entry.valid);

    // Channel colours are assigned from the full set, before any filter, so
    // narrowing to one channel never repaints the others.
    const netByChannel = new Map<string, number>();
    dated.forEach(({ row }) => {
      const channel = row.channel?.trim() || OTHER_CHANNEL;
      netByChannel.set(channel, (netByChannel.get(channel) ?? 0) + (row.net ?? 0));
    });
    const channelsByNet = Array.from(netByChannel.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([channel]) => channel);
    const scale = buildChannelScale(channelsByNet);

    // A quarter scopes the chart to flights *in market* during it, not just
    // those that launch in it -- a campaign running Jan to Dec is live in Q3.
    const window = filters.quarter ? quarterRange(filters.quarter) : null;

    const term = filters.search.trim().toLowerCase();

    /* Everything except the channel selection. Kept separate so the mix chart
       can show all channels while the timeline shows only the chosen one --
       collapsing that panel to a single bar would remove the control used to
       change the selection. */
    const matchesExceptChannel = ({
      row,
      start,
      end
    }: {
      row: CampaignTimelineRow;
      start: Dayjs;
      end: Dayjs;
    }): boolean => {
      if (window && (start.isAfter(window.end, 'day') || end.isBefore(window.start, 'day'))) {
        return false;
      }
      if (filters.status !== 'all' && statusOf(start, end, now) !== filters.status) return false;
      if (term) {
        const haystack = [row.campaign, row.channel, row.platform, row.trader]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    };

    const inScope = dated.filter(matchesExceptChannel);
    const matching = filters.channel
      ? inScope.filter(({ row }) => (row.channel?.trim() || OTHER_CHANNEL) === filters.channel)
      : inScope;

    // Totals describe what is on screen, so a KPI never contradicts the bars
    // sitting underneath it.
    const totals = matching.reduce(
      (acc, { row, start, end }) => {
        acc.net += row.net ?? 0;
        acc.lineItems += row.line_items ?? 0;
        acc[statusOf(start, end, now)] += 1;
        acc.days += end.diff(start, 'day') + 1;
        return acc;
      },
      { net: 0, lineItems: 0, live: 0, upcoming: 0, ended: 0, days: 0 }
    );

    if (!matching.length) {
      return {
        bars: [] as TimelineBar[],
        ticks: [] as AxisTick[],
        majorTicks: [] as AxisTick[],
        channels: scale.named,
        colourOf: scale.colourOf,
        hasOther: scale.hasOther,
        todayLeft: null as number | null,
        totals,
        volume: [] as { key: string; label: string; live: number; net: number }[],
        channelMix: [] as { name: string; value: number; count: number; colour: string }[],
        hasRows: dated.length > 0,
        axisStart: now,
        axisEnd: now
      };
    }

    const unit = filters.zoom === 'week' ? 'week' : 'month';
    // Inside a chosen quarter the axis is exactly that quarter, so the columns
    // are the three months being asked about rather than the ragged extent of
    // whatever long-running flights happen to overlap it.
    const axisStart = window
      ? window.start.startOf(unit)
      : dayjs(Math.min(...matching.map((entry) => entry.start.valueOf()))).startOf(unit);
    const axisEnd = window
      ? window.end.endOf(unit)
      : dayjs(Math.max(...matching.map((entry) => entry.end.valueOf()))).endOf(unit);
    // Span in real days, so leap years and short months stay honest.
    const totalDays = Math.max(axisEnd.diff(axisStart, 'day') + 1, 1);
    const pctPerDay = 100 / totalDays;

    const ticks: AxisTick[] = [];
    const majorTicks: AxisTick[] = [];

    if (filters.zoom === 'week') {
      let cursor = axisStart;
      let currentMonth = '';
      while (cursor.isBefore(axisEnd)) {
        const next = cursor.add(1, 'week');
        const days = Math.min(next.diff(cursor, 'day'), axisEnd.diff(cursor, 'day') + 1);
        ticks.push({
          key: cursor.format('YYYY-MM-DD'),
          label: cursor.format('D'),
          weight: days / totalDays,
          major: false
        });
        const month = cursor.format('MMM YYYY');
        const last = majorTicks[majorTicks.length - 1];
        if (last && last.label === month) last.weight += days / totalDays;
        else majorTicks.push({ key: month, label: month, weight: days / totalDays, major: true });
        currentMonth = month;
        cursor = next;
      }
      void currentMonth;
    } else {
      let cursor = axisStart;
      while (cursor.isBefore(axisEnd)) {
        const next = cursor.add(1, 'month');
        const days = next.diff(cursor, 'day');
        const quarter = `Q${Math.floor(cursor.month() / 3) + 1} ${cursor.format('YYYY')}`;
        ticks.push({
          key: cursor.format('YYYY-MM'),
          // At quarter zoom the months are too narrow for a name, so they carry
          // an initial and the quarter band above does the labelling.
          label: filters.zoom === 'quarter' ? cursor.format('MMM')[0] : cursor.format('MMM'),
          weight: days / totalDays,
          major: cursor.month() % 3 === 0
        });
        const last = majorTicks[majorTicks.length - 1];
        if (last && last.label === quarter) last.weight += days / totalDays;
        else majorTicks.push({ key: quarter, label: quarter, weight: days / totalDays, major: true });
        cursor = next;
      }
    }

    const bars: TimelineBar[] = matching.map(({ row, start, end }, index) => {
      // End date is inclusive: the 1st to the 1st is one day, not zero.
      const days = end.diff(start, 'day') + 1;
      const rawLeft = start.diff(axisStart, 'day') * pctPerDay;
      const rawRight = (end.diff(axisStart, 'day') + 1) * pctPerDay;
      // A flight that starts before or ends after the visible window is drawn
      // to the edge rather than off it, so it stays on screen and legible.
      const left = Math.max(rawLeft, 0);
      const right = Math.min(rawRight, 100);
      return {
        key: `${row.campaign ?? ''}|${row.channel ?? ''}|${row.platform ?? ''}|${row.start_date ?? ''}|${index}`,
        row,
        left,
        width: Math.max(right - left, 0.35),
        colour: scale.colourOf(row.channel?.trim() || OTHER_CHANNEL),
        status: statusOf(start, end, now),
        start,
        end,
        days,
        clippedStart: rawLeft < -0.01,
        clippedEnd: rawRight > 100.01
      };
    });

    const sorters: Record<SortKey, (a: TimelineBar, b: TimelineBar) => number> = {
      start: (a, b) => a.left - b.left || a.days - b.days,
      net: (a, b) => (b.row.net ?? 0) - (a.row.net ?? 0),
      duration: (a, b) => b.days - a.days,
      campaign: (a, b) => (a.row.campaign ?? '').localeCompare(b.row.campaign ?? '')
    };
    bars.sort(sorters[filters.sort]);

    // Concurrency over time: how many flights are in market in each bucket, and
    // what they are worth. Net is apportioned by the share of the flight that
    // falls inside the bucket, so a year-long booking is not counted whole in
    // every month it touches.
    // Boundaries are accumulated in whole days off the axis start. Re-deriving
    // them from each tick's fractional weight rounds every step and drifts, so
    // the cumulative day offset is carried alongside the ticks instead.
    let dayCursor = 0;
    const volume = ticks.map((tick) => {
      const bucketStart = axisStart.add(dayCursor, 'day');
      const bucketDays = Math.round(tick.weight * totalDays);
      dayCursor += bucketDays;
      let live = 0;
      let net = 0;
      matching.forEach(({ row, start, end }) => {
        // Both ends inclusive, counted in whole days: a flight that covers only
        // the last day of a bucket still counts as one day in it, not zero.
        const overlapStart = Math.max(start.valueOf(), bucketStart.valueOf());
        const overlapEnd = Math.min(end.valueOf(), bucketStart.add(bucketDays - 1, 'day').valueOf());
        if (overlapEnd < overlapStart) return;
        live += 1;
        const flightDays = end.diff(start, 'day') + 1;
        const overlapDays = Math.round((overlapEnd - overlapStart) / DAY_MS) + 1;
        net += ((row.net ?? 0) * Math.min(overlapDays, flightDays)) / flightDays;
      });
      return {
        key: tick.key,
        label:
          filters.zoom === 'week'
            ? bucketStart.format('D MMM')
            : bucketStart.format(filters.zoom === 'quarter' ? 'MMM YY' : 'MMM YYYY'),
        live,
        net
      };
    });

    const mixTotals = new Map<string, { value: number; count: number }>();
    inScope.forEach(({ row }) => {
      const channel = row.channel?.trim() || OTHER_CHANNEL;
      const entry = mixTotals.get(channel) ?? { value: 0, count: 0 };
      entry.value += row.net ?? 0;
      entry.count += 1;
      mixTotals.set(channel, entry);
    });
    const channelMix = Array.from(mixTotals.entries())
      .map(([name, entry]) => ({
        name,
        value: entry.value,
        count: entry.count,
        colour: scale.colourOf(name)
      }))
      .sort((a, b) => b.value - a.value || b.count - a.count);

    const todayOffset = now.startOf('day').diff(axisStart, 'day') * pctPerDay;

    return {
      bars,
      ticks,
      majorTicks,
      channels: channelsByNet,
      colourOf: scale.colourOf,
      hasOther: scale.hasOther,
      todayLeft: todayOffset >= 0 && todayOffset <= 100 ? todayOffset : null,
      totals,
      volume,
      channelMix,
      hasRows: dated.length > 0,
      axisStart,
      axisEnd
    };
  }, [rows, filters]);
};
